import { Link } from 'react-router-dom';
import ReportData from './ReportData/ReportData';
import { TabPanel, Tabs } from '@round/ui-kit';
import usePlatformHashSelect, { getDefaultPlatform } from 'Modules/Plans/hooks/usePlatformHashSelect';
import PlatformTabs from 'Modules/Plans/components/PlatformTabs/PlatformTabs';
import styles from './Report.module.css';
import ReportInstagramCampaigns from './Campaigns/InstagramCampaigns/ReportInstagramCampaigns';
import ReportYoutubeCampaigns from './Campaigns/YoutubeCampaigns/ReportYoutubeCampaigns';
import PlatformHeadlineStats from './PlatformHeadlineStats/PlatformHeadlineStats';
import { useReport } from '@round/creatorbase-helpers/api-hooks';
import { creatorbase } from '@round/api';
import ProjectSelectModal from './ProjectSelect/ProjectSelectModal';
import useReportId from './hooks/useReportId';
import useUrlState from 'Hooks/useUrlState';
import capitalize from 'lodash/capitalize';
import { Helmet } from 'react-helmet';
import TikTokTab from './Tabs/TikTokTab/TikTokTab';
import { assets } from '@round/whitelabel-ui';

const { ChevronDownFilled: ChevronDownFilledIcon } = assets;

const platforms: creatorbase.Platform[] = ['instagram', 'tiktok', 'youtube'];

const platformCampaignsComponentMap = {
    instagram: ReportInstagramCampaigns,
    tiktok: TikTokTab,
    youtube: ReportYoutubeCampaigns,
};

type UrlState = {
    isEdit: string | undefined;
};

const Report = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();

    const id = useReportId();
    const { data: report } = useReport(id);

    const [activeTab, setActiveTab] = usePlatformHashSelect((goToPlatform) => {
        if (!report) {
            return;
        }

        goToPlatform(getDefaultPlatform(report.platforms), true);
    });

    const helmetTitle = report?.name
        ? `${report.name} · Report ${activeTab ? ` · ${capitalize(activeTab)}` : ''}`
        : null;

    return (
        <Tabs activeTab={activeTab} onChange={setActiveTab}>
            <Helmet>{helmetTitle && <title>{helmetTitle}</title>}</Helmet>
            <header className={styles.header}>
                <div className={styles.goBackContainer}>
                    <Link className={styles.goBackButton} to="/c/reports/">
                        <ChevronDownFilledIcon className={styles.goBackButtonIcon} />
                        Reports
                    </Link>
                </div>

                <ReportData className={styles.reportData} onClickEditReport={() => setUrlState({ isEdit: 'true' })} />

                <PlatformTabs className={styles.tabsNav}>
                    {platforms.map((platform) => (
                        <PlatformTabs.Tab key={platform} name={platform} platform={platform} />
                    ))}
                </PlatformTabs>
            </header>

            <main>
                {platforms.map((platform) => {
                    const ReportPlatformCampaigns = platformCampaignsComponentMap[platform];

                    return (
                        <TabPanel key={platform} name={platform} data-draggable-container>
                            <PlatformHeadlineStats platform={platform} />

                            <section className={styles.campaigns}>
                                <ReportPlatformCampaigns />
                            </section>
                        </TabPanel>
                    );
                })}
            </main>

            <ProjectSelectModal
                isOpen={urlState.isEdit === 'true'}
                onClose={() => setUrlState({ isEdit: undefined })}
                onSuccess={(updated) => {
                    if (!report?.platforms.length && updated.platforms.length) {
                        // this code exists, because closing a modal and updating platform
                        // conflicts (overrides each other) at URL level
                        setUrlState({ isEdit: undefined, hash: getDefaultPlatform(updated.platforms) });
                        return;
                    }

                    setUrlState({ isEdit: undefined });
                }}
            />
        </Tabs>
    );
};

export default Report;
